/**
 * Edit Cattle Page - Edit existing cattle entries
 * Updated to use master data IDs and proper field mappings for backend integration
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, ArrowLeft, Scan, Upload, Camera, FileText,
  User, Activity, Baby, Globe, Settings, Ruler,
  AlertCircle, CheckCircle, Clock, Plus, X
} from 'lucide-react';
import {
  gauShalaApi,
  calculateDobFromAge,
  calculateAgeFromDob,
  type Breed,
  type Species,
  type Gender,
  type Color,
  type Cattle,
  type Source,
  type Ownership,
  type FeedType
} from '../../services/gaushala/api';
import { InputField, SelectField, TextAreaField, FileUploadField, MultiSelectField } from './CattleFormFields';

interface LanguageContextType {
  language: 'hi' | 'en';
  t: (key: string) => string;
}

interface EditCattleProps {
  languageContext: LanguageContextType;
}

export default function EditCattle({ languageContext }: EditCattleProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Validation constraints
  const VALIDATION_RULES = {
    weight: { min: 0, max: 2000, maxLength: 6 }, // Max 2000.0 kg (6 chars including decimal)
    height: { min: 0, max: 300, maxLength: 3 }   // Max 300 cm (3 chars, whole numbers only)
  };

  // Master data state
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [genders, setGenders] = useState<Gender[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [sheds, setSheds] = useState<any[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [ownerships, setOwnerships] = useState<Ownership[]>([]);
  const [feedTypes, setFeedTypes] = useState<FeedType[]>([]);
  const [masterDataLoading, setMasterDataLoading] = useState(true);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string | null>(null);

  // Track existing document paths for document count display
  const [existingDocuments, setExistingDocuments] = useState({
    photoPath: null as string | null,
    healthCertificatePath: null as string | null,
    vaccinationDocPath: null as string | null,
    purchaseDocPath: null as string | null
  });

  const [formData, setFormData] = useState({
    // Basic Identification - Updated to use IDs
    uniqueAnimalId: '',
    name: '',
    breedId: 0,
    speciesId: 0,
    genderId: 0,
    colorId: 0,
    ageYears: '',  // User enters age, will be converted to dob on submit
    dateOfEntry: new Date().toISOString().split('T')[0],
    // NOTE: gaushalaId is NOT included - backend extracts it from JWT token automatically

    // Health & Medical Records
    vaccinationStatus: '',
    disabilityStatus: '',
    veterinarianName: '',
    dewormingSchedule: '',
    lastHealthCheckup: '',
    veterinarianContact: '',
    medicalHistory: '',

    // Physical Characteristics - Updated field name
    weight: '',
    hornStatus: '',
    rfidTagNo: '',  // Changed from rfidTagNumber to match backend
    height: '',
    earTagNumber: '',
    microchipNumber: '',

    // Reproductive Details (Female only)
    milkingStatus: '',
    milkYieldPerDay: '',
    numberOfCalves: '',
    lactationNumber: '',
    lastCalvingDate: '',
    pregnancyStatus: '',

    // Origin & Ownership
    sourceOfAcquisition: '',
    previousOwner: '',
    dateOfAcquisition: '',
    ownershipStatus: '',

    // Shelter & Feeding
    shedNumber: '',
    feedTypeIds: [] as number[], // Changed from single typeOfFeed string to array of IDs
    feedingSchedule: '',

    // Supporting Documents
    photoFile: null as File | null,
    healthCertificate: null as File | null,
    vaccinationRecord: null as File | null,
    purchaseDocument: null as File | null
  });

  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState(0);

  // Store original values for duplicate check comparison
  const [originalUniqueAnimalId, setOriginalUniqueAnimalId] = useState<string>('');
  const [originalRfidTagNo, setOriginalRfidTagNo] = useState<string>('');

  // Duplicate check state
  const [duplicateCheckState, setDuplicateCheckState] = useState({
    uniqueAnimalId: { checking: false, exists: null as boolean | null },
    rfidTagNo: { checking: false, exists: null as boolean | null }
  });

  // Load master data and cattle record on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setMessage({ type: 'error', text: 'Invalid cattle ID' });
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Load master data and cattle record in parallel
        const [breedsRes, speciesRes, gendersRes, colorsRes, shedsRes, sourcesRes, ownershipsRes, feedTypesRes, cattleRes] = await Promise.all([
          gauShalaApi.masterData.getAllBreeds(),
          gauShalaApi.masterData.getAllSpecies(),
          gauShalaApi.masterData.getAllGenders(),
          gauShalaApi.masterData.getAllColors(),
          gauShalaApi.sheds.getAvailableSheds(),
          gauShalaApi.masterData.getAllSources(),
          gauShalaApi.masterData.getAllOwnership(),
          gauShalaApi.masterData.getAllFeedTypes(),
          gauShalaApi.cattle.getCattleById(parseInt(id))
        ]);

        if (breedsRes.success && breedsRes.data) setBreeds(breedsRes.data);
        if (speciesRes.success && speciesRes.data) setSpecies(speciesRes.data);
        if (gendersRes.success && gendersRes.data) setGenders(gendersRes.data);
        if (colorsRes.success && colorsRes.data) setColors(colorsRes.data);
        if (shedsRes.success && shedsRes.data) setSheds(shedsRes.data);
        if (sourcesRes.success && sourcesRes.data) setSources(sourcesRes.data);
        if (ownershipsRes.success && ownershipsRes.data) setOwnerships(ownershipsRes.data);
        if (feedTypesRes.success && feedTypesRes.data) setFeedTypes(feedTypesRes.data);

        if (cattleRes.success && cattleRes.data) {
          const cattle = cattleRes.data;
          // Store the current photo URL for display during edit
          if (cattle.photoUrl) {
            setCurrentPhotoUrl(cattle.photoUrl);
          }

          // Store existing document paths for document count display
          setExistingDocuments({
            photoPath: cattle.photoPath || cattle.photoUrl || null,
            healthCertificatePath: cattle.healthCertificatePath || null,
            vaccinationDocPath: cattle.vaccinationDocPath || null,
            purchaseDocPath: cattle.purchaseDocPath || null
          });

          setOriginalUniqueAnimalId(cattle.uniqueAnimalId);
          setOriginalRfidTagNo(cattle.rfidTagNo);
          // Transform backend data to form data (dob → ageYears, LocalDateTime → date strings)
          // NOTE: gaushalaId is NOT loaded - backend handles it from JWT automatically
          setFormData({
            uniqueAnimalId: cattle.uniqueAnimalId,
            name: cattle.name || '',
            breedId: cattle.breedId,
            speciesId: cattle.speciesId,
            genderId: cattle.genderId,
            colorId: cattle.colorId || 0,
            ageYears: String(calculateAgeFromDob(cattle.dob)),
            dateOfEntry: cattle.dateOfEntry ? cattle.dateOfEntry.split('T')[0] : new Date().toISOString().split('T')[0],

            // Health & Medical Records
            vaccinationStatus: cattle.vaccinationStatus || '',
            disabilityStatus: cattle.disabilityStatus || '',
            veterinarianName: cattle.vetName || '',
            dewormingSchedule: cattle.dewormingSchedule || '',
            lastHealthCheckup: cattle.lastHealthCheckupDate ? cattle.lastHealthCheckupDate.split('T')[0] : '',
            veterinarianContact: cattle.vetContact || '',
            medicalHistory: cattle.medicalHistory || cattle.healthStatus || '',

            // Physical Characteristics
            weight: cattle.weight?.toString() || '',
            hornStatus: cattle.hornStatus || '',
            rfidTagNo: cattle.rfidTagNo || '',
            height: cattle.height?.toString() || '',
            earTagNumber: cattle.earTagNo || '',
            microchipNumber: cattle.microchipNo || '',

            // Reproductive Details
            milkingStatus: cattle.milkingStatus || '',
            milkYieldPerDay: cattle.milkYieldPerDay?.toString() || '',
            numberOfCalves: cattle.calvesCount?.toString() || '',
            lactationNumber: cattle.lactationNumber?.toString() || '',
            lastCalvingDate: cattle.lastCalvingDate ? cattle.lastCalvingDate.split('T')[0] : '',
            pregnancyStatus: cattle.pregnancyStatus || '',

            // Origin & Ownership
            sourceOfAcquisition: cattle.sourceId?.toString() || '',
            previousOwner: cattle.previousOwner || '',
            dateOfAcquisition: cattle.dateOfAcquisition ? cattle.dateOfAcquisition.split('T')[0] : '',
            ownershipStatus: cattle.ownershipId?.toString() || '',

            // Shelter & Feeding
            shedNumber: cattle.shedNumber || '',
            feedTypeIds: cattle.feedTypeIds || [], // Load existing feed type IDs array
            feedingSchedule: cattle.feedingSchedule || '',

            // Supporting Documents
            photoFile: null,
            healthCertificate: null,
            vaccinationRecord: null,
            purchaseDocument: null
          });
        } else {
          setMessage({
            type: 'error',
            text: cattleRes.error || 'Failed to load cattle record.'
          });
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        setMessage({
          type: 'error',
          text: 'Failed to load cattle record. Please try again.'
        });
      } finally {
        setLoading(false);
        setMasterDataLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleInputChange = useCallback((field: string, value: string | File | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field - use functional update to prevent input unfocus bug
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });
    setMessage(null);
  }, []); // Empty dependency array - function is now stable

  const handleScanRfid = async () => {
    setIsScanning(true);
    try {
      const response = await gauShalaApi.cattle.scanRfid();
      if (response.success && response.data) {
        handleInputChange('rfidTagNo', response.data.rfidTag);

        if (response.data.cattleInfo) {
          setMessage({
            type: 'warning',
            text: 'RFID tag already exists for another cattle'
          });
        } else {
          setMessage({
            type: 'success',
            text: 'RFID tag scanned successfully'
          });
        }
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to scan RFID tag'
      });
    } finally {
      setIsScanning(false);
    }
  };

  // Real-time duplicate check for Unique Animal ID (only if it changed from original)
  const checkUniqueAnimalIdAvailability = useCallback(async (newId: string) => {
    // Only check if ID actually changed from original
    if (newId.trim() === originalUniqueAnimalId) {
      setDuplicateCheckState(prev => ({
        ...prev,
        uniqueAnimalId: { checking: false, exists: null }
      }));
      return;
    }

    if (!newId || newId.trim().length < 1) {
      setDuplicateCheckState(prev => ({
        ...prev,
        uniqueAnimalId: { checking: false, exists: null }
      }));
      return;
    }

    setDuplicateCheckState(prev => ({
      ...prev,
      uniqueAnimalId: { checking: true, exists: null }
    }));

    try {
      const response = await gauShalaApi.cattle.checkUniqueAnimalId(newId.trim());
      if (response.success && response.data) {
        setDuplicateCheckState(prev => ({
          ...prev,
          uniqueAnimalId: { checking: false, exists: response.data!.exists }
        }));

        // Set error if duplicate exists
        if (response.data.exists) {
          setErrors(prev => ({
            ...prev,
            uniqueAnimalId: 'This Unique Animal ID already exists'
          }));
        } else {
          setErrors(prev => {
            const { uniqueAnimalId, ...rest } = prev;
            return rest;
          });
        }
      }
    } catch (error) {
      console.warn('Error checking unique animal ID availability:', error);
      setDuplicateCheckState(prev => ({
        ...prev,
        uniqueAnimalId: { checking: false, exists: null }
      }));
    }
  }, [originalUniqueAnimalId]);

  // Real-time duplicate check for RFID Tag (only if it changed from original)
  const checkRfidTagAvailability = useCallback(async (newRfid: string) => {
    // Only check if RFID actually changed from original
    if (newRfid.trim() === originalRfidTagNo) {
      setDuplicateCheckState(prev => ({
        ...prev,
        rfidTagNo: { checking: false, exists: null }
      }));
      return;
    }

    if (!newRfid || newRfid.trim().length < 3) {
      setDuplicateCheckState(prev => ({
        ...prev,
        rfidTagNo: { checking: false, exists: null }
      }));
      return;
    }

    setDuplicateCheckState(prev => ({
      ...prev,
      rfidTagNo: { checking: true, exists: null }
    }));

    try {
      const response = await gauShalaApi.cattle.checkRfidTag(newRfid.trim());
      if (response.success && response.data) {
        setDuplicateCheckState(prev => ({
          ...prev,
          rfidTagNo: { checking: false, exists: response.data!.exists }
        }));

        // Set error if duplicate exists
        if (response.data.exists) {
          setErrors(prev => ({
            ...prev,
            rfidTagNo: 'This RFID Tag already exists'
          }));
        } else {
          setErrors(prev => {
            const { rfidTagNo, ...rest } = prev;
            return rest;
          });
        }
      }
    } catch (error) {
      console.warn('Error checking RFID tag availability:', error);
      setDuplicateCheckState(prev => ({
        ...prev,
        rfidTagNo: { checking: false, exists: null }
      }));
    }
  }, [originalRfidTagNo]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields validation - Updated for ID fields
    if (!formData.uniqueAnimalId.trim()) newErrors.uniqueAnimalId = 'Unique Animal ID is required';
    if (formData.breedId === 0) newErrors.breedId = 'Breed is required';
    if (formData.speciesId === 0) newErrors.speciesId = 'Species is required';
    if (formData.genderId === 0) newErrors.genderId = 'Gender is required';
    // Age validation
    if (!formData.ageYears || formData.ageYears === '') {
      newErrors.ageYears = 'Please enter the age';
    } else {
      const ageVal = parseInt(formData.ageYears);
      if (isNaN(ageVal) || ageVal < 0 || ageVal > 30) {
        newErrors.ageYears = 'Age must be between 0 and 30 years';
      }
    }
    if (!formData.dateOfEntry) newErrors.dateOfEntry = 'Date of Entry is required';
    if (!formData.rfidTagNo.trim()) newErrors.rfidTagNo = 'RFID Tag Number is required';
    if (!formData.disabilityStatus) newErrors.disabilityStatus = 'Health Status is required';

    // Validate weight and height (optional fields, but if provided must be valid)
    if (formData.weight) {
      const weightValue = parseFloat(formData.weight);
      if (isNaN(weightValue)) {
        newErrors.weight = 'Weight must be a valid number';
      } else if (weightValue < VALIDATION_RULES.weight.min || weightValue > VALIDATION_RULES.weight.max) {
        newErrors.weight = `Weight must be between ${VALIDATION_RULES.weight.min} and ${VALIDATION_RULES.weight.max} kg`;
      }
    }

    if (formData.height) {
      const heightValue = parseFloat(formData.height);
      if (isNaN(heightValue)) {
        newErrors.height = 'Height must be a valid number';
      } else if (heightValue < VALIDATION_RULES.height.min || heightValue > VALIDATION_RULES.height.max) {
        newErrors.height = `Height must be between ${VALIDATION_RULES.height.min} and ${VALIDATION_RULES.height.max} cm`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to convert date string (YYYY-MM-DD) to LocalDateTime format (YYYY-MM-DDTHH:MM:SS)
  const convertToLocalDateTime = (dateString: string): string | undefined => {
    if (!dateString || dateString.trim() === '') return undefined;
    // If already in ISO format, return as-is
    if (dateString.includes('T')) return dateString;
    // Convert date-only string to LocalDateTime at midnight
    return `${dateString}T00:00:00`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({
        type: 'error',
        text: 'Please fill in all required fields'
      });
      return;
    }

    if (!id) {
      setMessage({
        type: 'error',
        text: 'Invalid cattle ID'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      // Transform formData to match Cattle interface for backend - COMPLETE FIELD MAPPING
      // NOTE: gaushalaId is NOT sent - backend extracts it from JWT token automatically
      const cattleData: Omit<Cattle, 'id' | 'createdAt' | 'updatedAt' | 'gaushalaId'> = {
        uniqueAnimalId: formData.uniqueAnimalId,
        name: formData.name || undefined,
        breedId: formData.breedId,
        speciesId: formData.speciesId,
        genderId: formData.genderId,
        colorId: formData.colorId,
        dob: formData.ageYears && parseInt(formData.ageYears) > 0 ? calculateDobFromAge(parseInt(formData.ageYears)) : new Date().toISOString(),

        // Physical Characteristics
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        height: formData.height ? parseFloat(formData.height) : undefined,
        hornStatus: formData.hornStatus || undefined,
        rfidTagNo: formData.rfidTagNo,
        earTagNo: formData.earTagNumber || undefined,
        microchipNo: formData.microchipNumber || undefined,
        shedNumber: formData.shedNumber || undefined,

        // Health & Medical Records
        vaccinationStatus: formData.vaccinationStatus || undefined,
        disabilityStatus: formData.disabilityStatus || undefined,
        dewormingSchedule: formData.dewormingSchedule || undefined,
        medicalHistory: formData.medicalHistory || undefined,
        lastHealthCheckupDate: formData.lastHealthCheckup ? convertToLocalDateTime(formData.lastHealthCheckup) : undefined,
        vetName: formData.veterinarianName || undefined,
        vetContact: formData.veterinarianContact || undefined,

        // Reproductive Details
        milkingStatus: formData.milkingStatus || undefined,
        lactationNumber: formData.lactationNumber ? parseInt(formData.lactationNumber) : undefined,
        milkYieldPerDay: formData.milkYieldPerDay ? parseFloat(formData.milkYieldPerDay) : undefined,
        lastCalvingDate: formData.lastCalvingDate ? convertToLocalDateTime(formData.lastCalvingDate) : undefined,
        calvesCount: formData.numberOfCalves ? parseInt(formData.numberOfCalves) : undefined,
        pregnancyStatus: formData.pregnancyStatus || undefined,

        // Origin & Ownership
        sourceId: formData.sourceOfAcquisition ? parseInt(formData.sourceOfAcquisition) : undefined,
        dateOfAcquisition: formData.dateOfAcquisition ? convertToLocalDateTime(formData.dateOfAcquisition) : undefined,
        previousOwner: formData.previousOwner || undefined,
        ownershipId: formData.ownershipStatus ? parseInt(formData.ownershipStatus) : undefined,

        // Shelter & Feeding
        feedingSchedule: formData.feedingSchedule || undefined,
        feedTypeIds: formData.feedTypeIds && formData.feedTypeIds.length > 0 ? formData.feedTypeIds : undefined,

        // System fields
        dateOfEntry: convertToLocalDateTime(formData.dateOfEntry),
        isActive: true,
        totalDungCollected: 0,
        lastDungCollection: 0,
      };

      const response = await gauShalaApi.cattle.updateCattle(parseInt(id), cattleData);

      if (response.success && response.data) {
        // Upload documents if provided
        const uploadPromises = [];

        if (formData.photoFile) {
          uploadPromises.push(gauShalaApi.cattle.uploadPhoto(response.data.id, formData.photoFile));
        }

        if (formData.healthCertificate) {
          uploadPromises.push(gauShalaApi.cattle.uploadDocument(response.data.id, formData.healthCertificate, 'health_certificate'));
        }

        if (formData.vaccinationRecord) {
          uploadPromises.push(gauShalaApi.cattle.uploadDocument(response.data.id, formData.vaccinationRecord, 'vaccination_record'));
        }

        if (formData.purchaseDocument) {
          uploadPromises.push(gauShalaApi.cattle.uploadDocument(response.data.id, formData.purchaseDocument, 'purchase_document'));
        }

        // Wait for all uploads
        if (uploadPromises.length > 0) {
          try {
            await Promise.all(uploadPromises);
          } catch (uploadError) {
            console.warn('Some document uploads failed:', uploadError);
          }
        }

        setMessage({
          type: 'success',
          text: 'Cattle updated successfully!'
        });

        // Navigate back after delay - refresh both cattle and shed data if shed was changed
        setTimeout(() => {
          navigate('/gaushala/cattle', { state: { shouldRefresh: true, refreshSheds: true } });
        }, 2000);
      } else {
        throw new Error(response.error || 'Failed to update cattle');
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to update cattle. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/gaushala/cattle');
  };

  const sections = [
    {
      id: 'basic',
      title: 'Basic Identification',
      icon: User,
      color: 'from-blue-500 to-indigo-600',
      progress: 0
    },
    {
      id: 'physical',
      title: 'Physical Characteristics',
      icon: Ruler,
      color: 'from-purple-500 to-violet-600',
      progress: 0
    },
    {
      id: 'health',
      title: 'Health & Medical',
      icon: Activity,
      color: 'from-red-500 to-pink-600',
      progress: 0
    },
    {
      id: 'origin',
      title: 'Origin & Ownership',
      icon: Globe,
      color: 'from-emerald-500 to-teal-600',
      progress: 0
    },
    {
      id: 'shelter',
      title: 'Shelter & Feeding',
      icon: Settings,
      color: 'from-amber-500 to-yellow-600',
      progress: 0
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Loading cattle record...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back</span>
          </button>

          <div className="h-8 w-px bg-gray-300"></div>

          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🐄</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Edit Cattle Record</h2>
              <p className="text-gray-600">Update cattle information - ID #{id}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="cattle-form"
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Save className="h-4 w-4" />
            {isLoading ? 'Updating...' : 'Update Cattle'}
          </button>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between overflow-x-auto">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={section.id} className="flex items-center flex-shrink-0">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="ml-2">
                  <p className="text-xs font-medium text-gray-700">{section.title}</p>
                </div>
                {index < sections.length - 1 && (
                  <div className="w-8 h-px bg-gray-300 mx-3"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Message Alert */}
      {message && (
        <div className={`p-4 rounded-lg border-l-4 ${
          message.type === 'success'
            ? 'bg-green-50 border-green-400 text-green-800'
            : message.type === 'warning'
            ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
            : 'bg-red-50 border-red-400 text-red-800'
        }`}>
          <div className="flex items-center gap-3">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : message.type === 'warning' ? (
              <Clock className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span className="font-medium">{message.text}</span>
          </div>
        </div>
      )}
      <form id="cattle-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">

          {/* Left Section - 3 columns */}
          <div className="xl:col-span-3 space-y-6">

            {/* Basic Identification */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                🐄 Basic Identification
              </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <InputField
                    label="Unique Animal ID"
                    value={formData.uniqueAnimalId}
                    onChange={(value: string) => {
                      handleInputChange('uniqueAnimalId', value);
                      checkUniqueAnimalIdAvailability(value);
                    }}
                    placeholder="Enter unique animal ID"
                    required
                    error={errors.uniqueAnimalId}
                  />

                  <InputField
                    label="Name (Optional)"
                    value={formData.name}
                    onChange={(value: string) => handleInputChange('name', value)}
                    placeholder="Enter cattle name"
                  />

                  <SelectField
                    label="Breed"
                    value={formData.breedId}
                    onChange={(value: string) => handleInputChange('breedId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading breeds...' : 'Select Breed'}
                    required
                    error={errors.breedId}
                    options={breeds.filter(breed => breed && breed.id && breed.name).map(breed => ({
                      value: breed.id.toString(),
                      label: breed.name
                    }))}
                  />

                  <SelectField
                    label="Species"
                    value={formData.speciesId}
                    onChange={(value: string) => handleInputChange('speciesId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading species...' : 'Select Species'}
                    required
                    error={errors.speciesId}
                    options={species.filter(s => s && s.id && s.name).map(s => ({
                      value: s.id.toString(),
                      label: s.name
                    }))}
                  />

                  <SelectField
                    label="Gender"
                    value={formData.genderId}
                    onChange={(value: string) => handleInputChange('genderId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading genders...' : 'Select Gender'}
                    required
                    error={errors.genderId}
                    options={genders.filter(gender => gender && gender.id && gender.name).map(gender => ({
                      value: gender.id.toString(),
                      label: gender.name
                    }))}
                  />

                  <SelectField
                    label="Color"
                    value={formData.colorId}
                    onChange={(value: string) => handleInputChange('colorId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading colors...' : 'Select Color'}
                    error={errors.colorId}
                    options={colors.filter(color => color && color.id && color.name).map(color => ({
                      value: color.id.toString(),
                      label: color.name
                    }))}
                  />

                  <InputField
                    label="Age (Years)"
                    value={formData.ageYears}
                    onChange={(value: string) => {
                      const filtered = value.replace(/[^0-9]/g, '');
                      if (filtered === '') {
                        handleInputChange('ageYears', '');
                        return;
                      }
                      const numVal = parseInt(filtered);
                      if (numVal <= 30) {
                        handleInputChange('ageYears', String(numVal));
                      }
                    }}
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter age in years"
                    required
                    error={errors.ageYears}
                  />

                  <InputField
                    label="Date of Entry"
                    value={formData.dateOfEntry}
                    onChange={(value: string) => handleInputChange('dateOfEntry', value)}
                    type="date"
                    required
                    error={errors.dateOfEntry}
                  />
                </div>
              </div>

            {/* Physical Characteristics */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center">
                  <Ruler className="h-5 w-5 text-white" />
                </div>
                📏 Physical Characteristics
              </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Weight (kg)
                      <span className="text-xs text-gray-500 font-normal ml-2">(Max: 2000 kg)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.weight}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Allow only numbers and decimal point
                        value = value.replace(/[^0-9.]/g, '');

                        // Ensure only one decimal point
                        const parts = value.split('.');
                        if (parts.length > 2) {
                          value = parts[0] + '.' + parts.slice(1).join('');
                        }

                        // Allow empty string for clearing the field
                        if (value === '') {
                          handleInputChange('weight', '');
                          return;
                        }

                        // Validate length (max 6 characters including decimal)
                        if (value.length > VALIDATION_RULES.weight.maxLength) {
                          return;
                        }

                        // Validate numeric value range
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          if (numValue < VALIDATION_RULES.weight.min || numValue > VALIDATION_RULES.weight.max) {
                            setErrors(prev => ({
                              ...prev,
                              weight: `Weight must be between ${VALIDATION_RULES.weight.min} and ${VALIDATION_RULES.weight.max} kg`
                            }));
                          } else {
                            setErrors(prev => {
                              const { weight, ...rest } = prev;
                              return rest;
                            });
                          }
                        }

                        handleInputChange('weight', value);
                      }}
                      inputMode="decimal"
                      min={VALIDATION_RULES.weight.min}
                      max={VALIDATION_RULES.weight.max}
                      placeholder="Weight in kilograms"
                      className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 ${
                        errors.weight ? 'border-red-300 bg-red-50' :
                        formData.weight && parseFloat(formData.weight) >= VALIDATION_RULES.weight.min && parseFloat(formData.weight) <= VALIDATION_RULES.weight.max ? 'border-green-300' :
                        'border-gray-200'
                      }`}
                    />
                    {errors.weight && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.weight}
                      </p>
                    )}
                    {formData.weight && !errors.weight && parseFloat(formData.weight) >= VALIDATION_RULES.weight.min && parseFloat(formData.weight) <= VALIDATION_RULES.weight.max && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        Valid weight
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Height (cm)
                      <span className="text-xs text-gray-500 font-normal ml-2">(Max: 300 cm)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.height}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Allow only numbers (no decimal point for height)
                        value = value.replace(/[^0-9]/g, '');

                        // Allow empty string for clearing the field
                        if (value === '') {
                          handleInputChange('height', '');
                          return;
                        }

                        // Validate length (max 3 characters)
                        if (value.length > VALIDATION_RULES.height.maxLength) {
                          return;
                        }

                        // Validate numeric value range
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          if (numValue < VALIDATION_RULES.height.min || numValue > VALIDATION_RULES.height.max) {
                            setErrors(prev => ({
                              ...prev,
                              height: `Height must be between ${VALIDATION_RULES.height.min} and ${VALIDATION_RULES.height.max} cm`
                            }));
                          } else {
                            setErrors(prev => {
                              const { height, ...rest } = prev;
                              return rest;
                            });
                          }
                        }

                        handleInputChange('height', value);
                      }}
                      inputMode="numeric"
                      min={VALIDATION_RULES.height.min}
                      max={VALIDATION_RULES.height.max}
                      placeholder="Height in centimeters"
                      className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 ${
                        errors.height ? 'border-red-300 bg-red-50' :
                        formData.height && parseFloat(formData.height) >= VALIDATION_RULES.height.min && parseFloat(formData.height) <= VALIDATION_RULES.height.max ? 'border-green-300' :
                        'border-gray-200'
                      }`}
                    />
                    {errors.height && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.height}
                      </p>
                    )}
                    {formData.height && !errors.height && parseFloat(formData.height) >= VALIDATION_RULES.height.min && parseFloat(formData.height) <= VALIDATION_RULES.height.max && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        Valid height
                      </p>
                    )}
                  </div>

                  <SelectField
                    label="Horn Status"
                    value={formData.hornStatus}
                    onChange={(value: string) => handleInputChange('hornStatus', value)}
                    placeholder="Select Status"
                    options={[
                      { value: 'horned', label: 'Horned' },
                      { value: 'dehorned', label: 'Dehorned' },
                      { value: 'polled', label: 'Polled (Naturally hornless)' }
                    ]}
                  />

                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      RFID Tag Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={formData.rfidTagNo}
                        onChange={(e) => {
                          handleInputChange('rfidTagNo', e.target.value);
                          checkRfidTagAvailability(e.target.value);
                        }}
                        placeholder="Scan or enter RFID tag"
                        className={`flex-1 px-4 py-3.5 bg-white border-2 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 ${
                          errors.rfidTagNo ? 'border-red-300 bg-red-50' : 'border-gray-200'
                        }`}
                        required
                      />
                      <button
                        type="button"
                        onClick={handleScanRfid}
                        disabled={isScanning}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        <Scan className="h-5 w-5" />
                        {isScanning ? 'Scanning...' : 'Scan'}
                      </button>
                    </div>
                    {errors.rfidTagNo && (
                      <div className="flex items-center gap-2 text-sm text-red-600 mt-2">
                        <AlertCircle className="h-4 w-4" />
                        {errors.rfidTagNo}
                      </div>
                    )}
                  </div>

                  <InputField
                    label="Ear Tag Number"
                    value={formData.earTagNumber}
                    onChange={(value: string) => handleInputChange('earTagNumber', value)}
                    placeholder="Ear tag number"
                  />

                  <InputField
                    label="Microchip Number"
                    value={formData.microchipNumber}
                    onChange={(value: string) => handleInputChange('microchipNumber', value)}
                    placeholder="Microchip number"
                  />
                </div>
              </div>

            {/* Health & Medical Records */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                🩺 Health & Medical Records
              </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <InputField
                    label="Vaccination Status"
                    value={formData.vaccinationStatus}
                    onChange={(value: string) => {
                      const alphabetsOnly = value.replace(/[^a-zA-Z\s,.-]/g, '');
                      handleInputChange('vaccinationStatus', alphabetsOnly);
                    }}
                    placeholder="e.g., FMD, HS completed"
                  />

                  <SelectField
                    label="Health Status"
                    value={formData.disabilityStatus}
                    onChange={(value: string) => handleInputChange('disabilityStatus', value)}
                    placeholder="Select health status"
                    required={true}
                    error={errors.disabilityStatus}
                    options={[
                      { value: 'HEALTHY', label: 'Healthy' },
                      { value: 'SICK', label: 'Sick' },
                      { value: 'UNDER_TREATMENT', label: 'Under Treatment' }
                    ].sort((a, b) => a.label.localeCompare(b.label))}
                  />

                  <InputField
                    label="Veterinarian Name"
                    value={formData.veterinarianName}
                    onChange={(value: string) => {
                      const filtered = value.replace(/[^a-zA-Z\s.]/g, '');
                      handleInputChange('veterinarianName', filtered);
                    }}
                    placeholder="Name of attending veterinarian (alphabets only)"
                  />

                  <InputField
                    label="Veterinarian Contact"
                    value={formData.veterinarianContact}
                    onChange={(value: string) => {
                      const digitsOnly = value.replace(/[^0-9]/g, '');
                      if (digitsOnly.length <= 10) {
                        handleInputChange('veterinarianContact', digitsOnly);
                      }
                    }}
                    type="text"
                    inputMode="numeric"
                    placeholder="Phone number"
                    maxLength={10}
                  />

                  <InputField
                    label="Last Health Check-up"
                    value={formData.lastHealthCheckup}
                    onChange={(value: string) => handleInputChange('lastHealthCheckup', value)}
                    type="date"
                  />

                  <InputField
                    label="Deworming Schedule"
                    value={formData.dewormingSchedule}
                    onChange={(value: string) => handleInputChange('dewormingSchedule', value)}
                    placeholder="e.g., Every 6 months"
                  />

                  <TextAreaField
                    label="Medical History"
                    value={formData.medicalHistory}
                    onChange={(value: string) => handleInputChange('medicalHistory', value)}
                    placeholder="Previous medical treatments, surgeries, etc."
                    className="lg:col-span-3"
                  />
                </div>
              </div>

              {/* Additional Sections Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* Reproductive Details (Female only) - HIDDEN */}
                {/* This section is disabled to hide reproductive details for all cattle */}
                {false && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
                        <Baby className="h-5 w-5 text-white" />
                      </div>
                      🤱 Reproductive Details
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <SelectField
                        label="Milking Status"
                        value={formData.milkingStatus}
                        onChange={(value: string) => handleInputChange('milkingStatus', value)}
                        placeholder="Select Status"
                        options={[
                          { value: 'lactating', label: 'Lactating' },
                          { value: 'dry', label: 'Dry' },
                          { value: 'pregnant', label: 'Pregnant' },
                          { value: 'not_breeding', label: 'Not for Breeding' }
                        ]}
                      />

                      <InputField
                        label="Milk Yield Per Day (Liters)"
                        value={formData.milkYieldPerDay}
                        onChange={(value: string) => handleInputChange('milkYieldPerDay', value)}
                        type="number"
                        step="0.1"
                        placeholder="Daily milk yield"
                      />

                      <InputField
                        label="Number of Calves"
                        value={formData.numberOfCalves}
                        onChange={(value: string) => handleInputChange('numberOfCalves', value)}
                        type="number"
                        placeholder="Total calves born"
                      />

                      <InputField
                        label="Lactation Number"
                        value={formData.lactationNumber}
                        onChange={(value: string) => handleInputChange('lactationNumber', value)}
                        type="number"
                        placeholder="Current lactation number"
                      />

                      <InputField
                        label="Last Calving Date"
                        value={formData.lastCalvingDate}
                        onChange={(value: string) => handleInputChange('lastCalvingDate', value)}
                        type="date"
                      />

                      <SelectField
                        label="Pregnancy Status"
                        value={formData.pregnancyStatus}
                        onChange={(value: string) => handleInputChange('pregnancyStatus', value)}
                        placeholder="Select Status"
                        options={[
                          { value: 'pregnant', label: 'Pregnant' },
                          { value: 'not_pregnant', label: 'Not Pregnant' },
                          { value: 'unknown', label: 'Unknown' }
                        ]}
                      />
                    </div>
                  </div>
                )}

                {/* Origin & Ownership */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                      <Globe className="h-5 w-5 text-white" />
                    </div>
                    🌍 Origin & Ownership
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SelectField
                      label="Source of Acquisition"
                      value={formData.sourceOfAcquisition}
                      onChange={(value: string) => handleInputChange('sourceOfAcquisition', value)}
                      placeholder={masterDataLoading ? 'Loading sources...' : sources.length === 0 ? 'No sources available' : 'Select Source'}
                      options={sources.map(source => ({
                        value: String(source.id),
                        label: source.name || ''
                      })).sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                    />

                    <InputField
                      label="Previous Owner"
                      value={formData.previousOwner}
                      onChange={(value: string) => {
                        const filtered = value.replace(/[^a-zA-Z\s.]/g, '');
                        handleInputChange('previousOwner', filtered);
                      }}
                      placeholder="Name of previous owner (alphabets only)"
                    />

                    <InputField
                      label="Date of Acquisition"
                      value={formData.dateOfAcquisition}
                      onChange={(value: string) => handleInputChange('dateOfAcquisition', value)}
                      type="date"
                    />

                    <SelectField
                      label="Ownership Status"
                      value={formData.ownershipStatus}
                      onChange={(value: string) => handleInputChange('ownershipStatus', value)}
                      placeholder={masterDataLoading ? 'Loading statuses...' : ownerships.length === 0 ? 'No statuses available' : 'Select Status'}
                      options={ownerships.map(ownership => ({
                        value: String(ownership.id),
                        label: ownership.name || ''
                      })).sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                    />
                  </div>
                </div>

                {/* Shelter & Feeding */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center">
                      <Settings className="h-5 w-5 text-white" />
                    </div>
                    🏠 Shelter & Feeding
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SelectField
                      label="Shed Assignment"
                      value={formData.shedNumber}
                      onChange={(value: string) => handleInputChange('shedNumber', value)}
                      placeholder={masterDataLoading ? 'Loading sheds...' : sheds.length === 0 ? 'No sheds available' : 'Select Shed'}
                      options={sheds.map(shed => ({
                        value: shed.shedNumber,
                        label: `${shed.shedName} (${shed.currentOccupancy || 0}/${shed.capacity || 0} occupied)`
                      }))}
                    />

                    <MultiSelectField
                      label="Type of Feed"
                      value={formData.feedTypeIds.map(id => String(id))}
                      onChange={(values: string[]) => {
                        // Convert string values back to numbers for feedTypeIds
                        const numericIds = values.map(v => parseInt(v)).filter(n => !isNaN(n));
                        handleInputChange('feedTypeIds', numericIds);
                      }}
                      placeholder={masterDataLoading ? 'Loading feed types...' : feedTypes.length === 0 ? 'No feed types available' : 'Select Feed Types (multiple)'}
                      options={feedTypes.map(feedType => ({
                        value: String(feedType.id),
                        label: feedType.name || ''
                      })).sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                    />

                    <div className="md:col-span-2">
                      <TextAreaField
                        label="Feeding Schedule"
                        value={formData.feedingSchedule}
                        onChange={(value: string) => handleInputChange('feedingSchedule', value)}
                        placeholder="Feeding times and quantities"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

          {/* Right Sidebar */}
          <div className="xl:col-span-1 space-y-6">

            {/* Supporting Documents */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-slate-500 to-gray-600 rounded-full flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                📎 Documents
              </h2>

                <div className="space-y-6">
                  <FileUploadField
                    label="Photo"
                    value={formData.photoFile}
                    onChange={(value: File | null) => handleInputChange('photoFile', value)}
                    accept="image/*"
                    currentImage={currentPhotoUrl}
                  />

                  <FileUploadField
                    label="Health Certificate"
                    value={formData.healthCertificate}
                    onChange={(value: File | null) => handleInputChange('healthCertificate', value)}
                    accept=".pdf"
                    currentDocument={existingDocuments.healthCertificatePath}
                  />

                  <FileUploadField
                    label="Vaccination Record"
                    value={formData.vaccinationRecord}
                    onChange={(value: File | null) => handleInputChange('vaccinationRecord', value)}
                    accept=".pdf"
                    currentDocument={existingDocuments.vaccinationDocPath}
                  />

                  <FileUploadField
                    label="Purchase/Donation Document"
                    value={formData.purchaseDocument}
                    onChange={(value: File | null) => handleInputChange('purchaseDocument', value)}
                    accept=".pdf"
                    currentDocument={existingDocuments.purchaseDocPath}
                  />
                </div>

                {/* Quick Stats */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Form Progress</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Required Fields</span>
                      <span className="font-medium text-gray-900">
                        {Object.keys(errors).length === 0 ? '✓ Complete' : `${Object.keys(errors).length} missing`}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Documents</span>
                      <span className="font-medium text-gray-900">
                        {/* Count existing documents OR newly selected files for each document type */}
                        {[
                          formData.photoFile || existingDocuments.photoPath,
                          formData.healthCertificate || existingDocuments.healthCertificatePath,
                          formData.vaccinationRecord || existingDocuments.vaccinationDocPath,
                          formData.purchaseDocument || existingDocuments.purchaseDocPath
                        ].filter(Boolean).length}/4
                      </span>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
